import { useState, useRef, useEffect, useMemo } from "react";
import { icons } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { ChevronDown, Search, X } from "lucide-react";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import * as Accordion from "@radix-ui/react-accordion";
import { Text, Theme } from "@radix-ui/themes";

export type IconName = string;

interface IconPickerProps {
  value: string;
  onChange: (icon: string) => void;
}

export function IconPicker({ value, onChange }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [accordionValue, setAccordionValue] = useState<string>("");
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const initial_list_length = 100;

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setAccordionValue("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredIcons = useMemo(() => {
    const iconNames = Object.keys(icons);
    if (!search) return iconNames.slice(0, initial_list_length);

    const term = search.toLowerCase();
    return iconNames.filter((name) => name.toLowerCase().includes(term));
  }, [search]);

  const SelectedIcon = value ? icons[value as keyof typeof icons] : null;

  const toggleAccordion = () => {
    if (open) {
      setAccordionValue("");
      setTimeout(() => setOpen(false), 300);
    } else {
      setOpen(true);
      setAccordionValue("icon-picker");
    }
  };

  return (
    <Theme>
      <div ref={ref} style={{ width: "100%" }} className="mb-4">
        <Accordion.Root
          type="single"
          collapsible
          value={accordionValue}
          onValueChange={(value) => {
            setAccordionValue(value);
            setOpen(!!value);
          }}
        >
          <Accordion.Item value="icon-picker">
            <Accordion.Trigger asChild>
              <motion.div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "var(--space-2)",
                  padding: "var(--space-1) var(--space-2)",
                  border: "1px solid var(--gray-6)",
                  borderRadius: "var(--radius-2)",
                  backgroundColor: "var(--color-background)",
                  cursor: "pointer",
                  height: "var(--space-6)",
                  minHeight: "var(--space-6)",
                }}
                className="rt-BaseButton"
                layout
                onClick={toggleAccordion}
                whileHover={{ backgroundColor: "var(--gray-2)" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-2)",
                  }}
                >
                  {SelectedIcon ? (
                    <SelectedIcon style={{ width: "20px", height: "20px" }} />
                  ) : (
                    <Text style={{ color: "var(--gray-9)" }}>
                      Select an icon
                    </Text>
                  )}
                  <span style={{ fontSize: "var(--font-size-2)" }}>
                    {value}
                  </span>
                </div>
                <motion.div animate={{ rotate: open ? 180 : 0 }}>
                  <ChevronDown style={{ height: "16px", width: "16px" }} />
                </motion.div>
              </motion.div>
            </Accordion.Trigger>

            <AnimatePresence>
              {open && (
                <Accordion.Content asChild forceMount>
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{
                      height: "auto",
                      opacity: 1,
                      transition: {
                        height: { duration: 0.3 },
                        opacity: { duration: 0.2, delay: 0.1 },
                      },
                    }}
                    exit={{
                      height: 0,
                      opacity: 0,
                      transition: {
                        height: { duration: 0.3 },
                        opacity: { duration: 0.2 },
                      },
                    }}
                    style={{ overflow: "hidden" }}
                  >
                    <div
                      style={{
                        zIndex: 50,
                        width: "100%",
                        marginTop: "var(--space-1)",
                        backgroundColor: "var(--color-background)",
                        border: "1px solid var(--gray-6)",
                        borderRadius: "var(--radius-2)",
                        boxShadow: "var(--shadow-4)",
                      }}
                    >
                      {/* Search input */}
                      <div
                        style={{
                          position: "relative",
                          borderBottom: "1px solid var(--gray-6)",
                        }}
                      >
                        <Search
                          style={{
                            position: "absolute",
                            left: "var(--space-2)",
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: "16px",
                            height: "16px",
                            color: "var(--gray-9)",
                            pointerEvents: "none",
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Search icons..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          style={{
                            width: "100%",
                            paddingLeft: "var(--space-6)",
                            paddingRight: "var(--space-6)",
                            paddingTop: "var(--space-1)",
                            paddingBottom: "var(--space-1)",
                            backgroundColor: "transparent",
                            border: "none",
                            outline: "none",
                            fontSize: "var(--font-size-2)",
                            color: "var(--gray-12)",
                            height: "var(--space-6)",
                          }}
                          autoFocus
                        />
                        {search && (
                          <button
                            onClick={() => setSearch("")}
                            style={{
                              position: "absolute",
                              top: 0,
                              right: "var(--space-2)",
                              bottom: 0,
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              border: "none",
                              backgroundColor: "transparent",
                            }}
                            type="button"
                          >
                            <X
                              style={{
                                height: "16px",
                                width: "16px",
                                color: "var(--gray-9)",
                              }}
                            />
                          </button>
                        )}
                      </div>

                      {/* Status bar */}
                      <div
                        style={{
                          padding: "var(--space-2)",
                          fontSize: "var(--font-size-1)",
                          color: "var(--gray-10)",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>
                          {search
                            ? `${filteredIcons.length} matching icons`
                            : `First ${Math.min(
                                initial_list_length,
                                filteredIcons.length
                              )} of ${Object.keys(icons).length} icons`}
                        </span>
                      </div>

                      <ScrollArea.Root style={{ height: "384px" }}>
                        <ScrollArea.Viewport
                          style={{ width: "100%", height: "100%" }}
                        >
                          {filteredIcons.length === 0 ? (
                            <div
                              style={{
                                gridColumn: "span 4",
                                padding: "var(--space-4)",
                                textAlign: "center",
                                color: "var(--gray-10)",
                              }}
                            >
                              No icons found. Try a different search term.
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(4, 1fr)",
                                gap: "var(--space-2)",
                                padding: "var(--space-2)",
                              }}
                            >
                              {filteredIcons.map((name) => {
                                const Icon = icons[name as keyof typeof icons];
                                if (!Icon) return null;

                                return (
                                  <motion.button
                                    key={name}
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      padding: "var(--space-2)",
                                      borderRadius: "var(--radius-2)",
                                      border: "none",
                                      backgroundColor: "transparent",
                                      cursor: "pointer",
                                      color: "var(--gray-12)",
                                    }}
                                    onClick={() => {
                                      onChange(name);
                                      setOpen(false);
                                      setAccordionValue("");
                                    }}
                                    title={name}
                                    whileHover={{
                                      scale: 1.05,
                                      backgroundColor: "var(--gray-3)",
                                    }}
                                    whileTap={{ scale: 0.95 }}
                                    type="button"
                                  >
                                    <Icon
                                      style={{
                                        width: "24px",
                                        height: "24px",
                                        marginBottom: "var(--space-1)",
                                      }}
                                    />
                                    <span
                                      style={{
                                        fontSize: "10px",
                                        textAlign: "center",
                                        wordBreak: "break-word",
                                        width: "100%",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {name}
                                    </span>
                                  </motion.button>
                                );
                              })}
                            </div>
                          )}
                        </ScrollArea.Viewport>
                        <ScrollArea.Scrollbar
                          orientation="vertical"
                          style={{
                            width: "var(--space-2)",
                            backgroundColor: "var(--gray-4)",
                            borderRadius: "var(--radius-full)",
                          }}
                        >
                          <ScrollArea.Thumb
                            style={{
                              width: "100%",
                              backgroundColor: "var(--gray-8)",
                              borderRadius: "var(--radius-full)",
                            }}
                          />
                        </ScrollArea.Scrollbar>
                      </ScrollArea.Root>
                    </div>
                  </motion.div>
                </Accordion.Content>
              )}
            </AnimatePresence>
          </Accordion.Item>
        </Accordion.Root>
      </div>
    </Theme>
  );
}
